// ============================================================================
// Routing Conditions - What triggers a rule
// ============================================================================

export interface TimeCondition {
  type: 'time';
  start_time: string; // HH:MM format
  end_time: string;
  days: string[]; // ['monday', 'tuesday', etc.]
  timezone: string;
}

export interface GeographicCondition {
  type: 'geographic';
  countries: string[]; // ISO 3166-1 alpha-2 codes
  regions: string[];
  cities: string[];
}

export interface DeviceCondition {
  type: 'device';
  device_types: string[]; // ['mobile', 'desktop', 'tablet']
  operating_systems: string[]; // ['windows', 'macos', 'linux', 'ios', 'android']
  browsers: string[]; // ['chrome', 'firefox', 'safari', 'edge']
}

export type RoutingCondition = TimeCondition | GeographicCondition | DeviceCondition;

export interface ConditionGroup {
  operator: 'AND' | 'OR';
  items: RoutingCondition[];
}

// ============================================================================
// Routing Actions - What happens when a rule matches
// ============================================================================

export interface RedirectAction {
  type: 'redirect';
  url: string;
}

export interface RandomizedUrl {
  url: string;
  weight: number;
}

export interface RandomizeAction {
  type: 'randomize';
  urls: RandomizedUrl[];
}

export interface ImageAction {
  type: 'image';
  image_id: number;
}

export type RoutingAction = RedirectAction | RandomizeAction | ImageAction;

// ============================================================================
// Routing Rule - Complete rule definition
// ============================================================================

export interface RoutingRule {
  id: string;
  name: string;
  priority: number;
  enabled: boolean;
  conditions: ConditionGroup;
  action: RoutingAction;
}

export interface RoutingRulesConfig {
  version: number;
  rules: RoutingRule[];
}

// ============================================================================
// QR Code Types
// ============================================================================

export type QRDotStyle = 'square' | 'dots' | 'rounded' | 'classy' | 'classy-rounded' | 'extra-rounded';
export type QRCornerSquareStyle = 'square' | 'dot' | 'extra-rounded';
export type QRCornerDotStyle = 'square' | 'dot';

export interface QRStyleOptions {
  dots_type?: QRDotStyle;
  corners_square_type?: QRCornerSquareStyle;
  corners_dot_type?: QRCornerDotStyle;
}

export const DEFAULT_QR_STYLE: Required<QRStyleOptions> = {
  dots_type: 'square',
  corners_square_type: 'square',
  corners_dot_type: 'square',
};

export interface UTMParameters {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
}

export interface QRCodeCreate {
  title: string;
  description?: string;
  fallback_url: string;
  routing_rules?: RoutingRulesConfig;
  custom_domain?: string;
  utm_parameters?: UTMParameters | null;
  password?: string;
  expires_at?: string;
  max_scans?: number;
  custom_logo_url?: string;
  logo_image_id?: number | null;
  fallback_image_id?: number | null;
  background_color?: string;
  foreground_color?: string;
  qr_style?: QRStyleOptions;
  tag_ids?: number[];
}

export interface QRCodeUpdate {
  title?: string;
  description?: string;
  fallback_url?: string;
  routing_rules?: RoutingRulesConfig;
  custom_domain?: string;
  utm_parameters?: UTMParameters | null;
  is_active?: boolean;
  password?: string;
  expires_at?: string;
  max_scans?: number;
  custom_logo_url?: string;
  logo_image_id?: number | null;
  fallback_image_id?: number | null;
  background_color?: string;
  foreground_color?: string;
  qr_style?: QRStyleOptions;
  tag_ids?: number[];
}

export interface QRTag {
  id: number;
  user_id: number;
  name: string;
  color?: string;
  created_at: string;
}

export interface QRCodeResponse {
  id: number;
  user_id: number;
  short_code: string;
  title: string;
  description?: string;
  fallback_url: string;
  routing_rules?: RoutingRulesConfig;
  custom_domain?: string;
  utm_parameters?: UTMParameters | null;
  is_active: boolean;
  password_protected: boolean;
  expires_at?: string;
  max_scans?: number;
  custom_logo_url?: string;
  logo_image_id?: number | null;
  logo_image_url?: string | null;
  fallback_image_id?: number | null;
  fallback_image_url?: string | null;
  background_color?: string;
  foreground_color?: string;
  qr_style?: QRStyleOptions;
  created_at: string;
  updated_at: string;
  tags: QRTag[];
  // Computed fields
  has_rules: boolean;
  rules_count: number;
}

export interface QRCodeDetailResponse extends QRCodeResponse {
}

export interface QRCodeListResponse {
  items: QRCodeResponse[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

export interface QRTagCreate {
  name: string;
  color?: string;
}

export interface QRTagUpdate extends Partial<QRTagCreate> {}

export interface QRTagResponse extends QRTag {}

// ============================================================================
// Analytics Types
// ============================================================================

export interface AnalyticsSummary {
  total_scans: number;
  unique_users: number;
  today_scans: number;
  week_scans: number;
  month_scans: number;
  last_scan_at?: string;
}

export interface AnalyticsDeviceBreakdown {
  device_type: string;
  count: number;
  percentage: number;
}

export interface AnalyticsGeographicBreakdown {
  country_code: string;
  country_name: string;
  count: number;
  percentage: number;
}

export interface AnalyticsTimeSeries {
  date: string;
  scans: number;
  unique_users: number;
}

export interface QRCodeAnalyticsResponse {
  summary: AnalyticsSummary;
  device_breakdown: AnalyticsDeviceBreakdown[];
  geographic_breakdown: AnalyticsGeographicBreakdown[];
  time_series: AnalyticsTimeSeries[];
  period: string;
}

// ============================================================================
// Helper Types for Rule Builder UI
// ============================================================================

export type ConditionType = 'time' | 'geographic' | 'device';
export type ActionType = 'redirect' | 'randomize' | 'image';

export const VALID_DAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const;

export const VALID_DEVICE_TYPES = ['mobile', 'desktop', 'tablet'] as const;

export const VALID_OPERATING_SYSTEMS = [
  'windows',
  'macos',
  'linux',
  'ios',
  'android',
] as const;

export const VALID_BROWSERS = [
  'chrome',
  'firefox',
  'safari',
  'edge',
  'opera',
] as const;

// Country list (top countries for convenience)
export const COMMON_COUNTRIES = [
  { code: 'US', name: 'United States' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'CA', name: 'Canada' },
  { code: 'AU', name: 'Australia' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'JP', name: 'Japan' },
  { code: 'CN', name: 'China' },
  { code: 'IN', name: 'India' },
  { code: 'BR', name: 'Brazil' },
  { code: 'MX', name: 'Mexico' },
  { code: 'ES', name: 'Spain' },
  { code: 'IT', name: 'Italy' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'SE', name: 'Sweden' },
  { code: 'NO', name: 'Norway' },
  { code: 'DK', name: 'Denmark' },
  { code: 'FI', name: 'Finland' },
  { code: 'PL', name: 'Poland' },
  { code: 'CH', name: 'Switzerland' },
] as const;

// Helper function to create a new empty rule
export function createEmptyRule(priority: number = 1): RoutingRule {
  return {
    id: crypto.randomUUID(),
    name: 'New Rule',
    priority,
    enabled: true,
    conditions: {
      operator: 'AND',
      items: [],
    },
    action: {
      type: 'redirect',
      url: '',
    },
  };
}

// Helper function to get browser's timezone with fallback
function getBrowserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

// Helper function to create empty conditions
export function createEmptyTimeCondition(): TimeCondition {
  return {
    type: 'time',
    start_time: '09:00',
    end_time: '17:00',
    days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
    timezone: getBrowserTimezone(),
  };
}

export function createEmptyGeographicCondition(): GeographicCondition {
  return {
    type: 'geographic',
    countries: [],
    regions: [],
    cities: [],
  };
}

export function createEmptyDeviceCondition(): DeviceCondition {
  return {
    type: 'device',
    device_types: [],
    operating_systems: [],
    browsers: [],
  };
}
